import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Tooltip from "@mui/material/Tooltip";
import { brand } from "../../theme/themePrimitives";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { BarChart } from "@mui/x-charts/BarChart";
import { apiService } from "../../services/api";

interface BreakdownData {
  months: string[];
  highPerforming: number[];
  average: number[];
  needsImprovement: number[];
  totalCalls: number;
  trend: number | null;
}

interface Props {
  salespersonId?: string;
}

export default function CallsVolumeChart({ salespersonId }: Props = {}) {
  const [data, setData] = useState<BreakdownData | null>(null);

  useEffect(() => {
    const fetch = salespersonId
      ? apiService.getSalespersonMonthlyBreakdown(salespersonId)
      : apiService.getCallMonthlyBreakdown();
    fetch.then((res) => {
      if (res.success && res.data) {
        setData(res.data);
      }
    });
  }, [salespersonId]);

  const colorPalette = [brand[700], brand[500], brand[200]];

  const months = data?.months ?? ["", "", "", "", "", ""];
  const highPerforming = data?.highPerforming ?? new Array(6).fill(0);
  const average = data?.average ?? new Array(6).fill(0);
  const needsImprovement = data?.needsImprovement ?? new Array(6).fill(0);
  const totalCalls = data?.totalCalls ?? 0;
  const trend = data?.trend ?? null;

  const maxStackedTotal = months.reduce((max, _, i) => {
    const stackTotal = highPerforming[i] + average[i] + needsImprovement[i];
    return Math.max(max, stackTotal);
  }, 0);
  const yAxisMax = maxStackedTotal > 0 ? maxStackedTotal + 1 : 5;

  let trendLabel = "";
  let trendColor: "success" | "error" | "default" = "default";
  if (trend !== null) {
    trendLabel = `${trend >= 0 ? "+" : ""}${trend}%`;
    trendColor = trend >= 0 ? "success" : "error";
  }

  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          Calls Analyzed
        </Typography>
        <Stack sx={{ justifyContent: "space-between" }}>
          <Stack
            direction="row"
            sx={{
              alignContent: { xs: "center", sm: "flex-start" },
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="h4" component="p">
              {totalCalls.toLocaleString()}
            </Typography>
            {trendLabel && (
              <Tooltip
                title="Change in total calls analyzed vs previous month"
                arrow
              >
                <Chip size="small" color={trendColor} label={trendLabel} />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Calls analyzed for the last 6 months
          </Typography>
        </Stack>
        <BarChart
          borderRadius={8}
          colors={colorPalette}
          xAxis={[
            {
              scaleType: "band",
              categoryGapRatio: 0.5,
              data: months,
              height: 24,
            },
          ]}
          yAxis={[{ width: 50, min: 0, max: yAxisMax }]}
          series={[
            {
              id: "needs-improvement",
              label: "Needs Improvement (<6)",
              data: needsImprovement,
              stack: "total",
            },
            {
              id: "average",
              label: "Average (6–8)",
              data: average,
              stack: "total",
            },
            {
              id: "high-performing",
              label: "High Performing (>8)",
              data: highPerforming,
              stack: "total",
            },
          ]}
          height={250}
          margin={{ left: 0, right: 0, top: 20, bottom: 0 }}
          grid={{ horizontal: true }}
          sx={{
            "& .MuiChartsAxis-line": {
              stroke: "#9ca3af",
            },
            "& .MuiChartsAxis-tick": {
              stroke: "#9ca3af",
            },
            "& .MuiChartsAxis-tickLabel": {
              fill: "#6b7280 !important",
              fontWeight: 500,
            },
            "& .MuiChartsGrid-line": {
              stroke: "#e5e7eb",
              strokeDasharray: "4 2",
              strokeWidth: 0.8,
            },
          }}
          hideLegend
        />
      </CardContent>
    </Card>
  );
}
